import { useEffect, useRef } from 'react';
import { log } from 'log';
import { pick, prop } from 'ramda';
import { useDispatch } from 'react-redux';
import { isExternalUrl, openWindow } from 'js/utils/url.util';
import { htcmdService } from 'js/services/htcmd.service';
import { useNavigate } from 'js/hooks/navigate/useNavigate';
import { useLoginPopup } from 'js/components/popups/LoginPopup';
import { useAppSelector, useUserSelector, useSportsbookSelector } from 'js/store/hooks';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { setPopupDynamicPagePath } from 'js/components/system/Page/store/actions';
import { useAddSubscription, useGetPlayerTags, usePushSubscription } from 'server';

import * as actions from '../store/actions';
import { useSportsbookGetTemptoken } from './useSportsbookGetTemptoken';

export const useSportsbookListeners = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const showLoginPopup = useLoginPopup();
    const {
        depositPage,
        loginPage
    } = useAppSelector(pick(['depositPage', 'loginPage']));
    const sportsbookGetTemptoken = useSportsbookGetTemptoken();
    const {
        isAppInitialized,
        isRenderingStarted
    } = useSportsbookSelector(pick(['isAppInitialized', 'isRenderingStarted']));
    const sportsbookRootURL = useAppSelector(prop('sportsbookRootURL'));
    const {
        isSessionStarted,
        windowSessionId,
        isLoggedIn
    } = useUserSelector(pick(['isSessionStarted', 'windowSessionId', 'isLoggedIn']));
    const addSubscription = useAddSubscription();
    const getPlayerTags = useGetPlayerTags();
    const sportsTagsRef = useRef([]);

    useEffect(() => {
        if (!isAppInitialized || !isRenderingStarted) {
            return;
        }

        try {
            window.Sportsbook.setListener('SHOW_LOGIN', (...props) => {
                if (isLoggedIn) {
                    window.Sportsbook.sendMessage('SYNC_SESSION');
                } else if (htcmdService.isEnabled) {
                    htcmdService.openLogin();
                } else {
                    if (loginPage) {
                        navigate(loginPage);
                    } else {
                        showLoginPopup(props);
                    }
                }
            });
            window.Sportsbook.setListener('INTERNAL_ROUTE', (route) => dispatch(setPopupDynamicPagePath(route || sportsbookRootURL)));
            window.Sportsbook.setListener('BALANCE_UPDATED', (balance) => dispatch(actions.updateBalances(balance)));
            window.Sportsbook.setListener('GET_SESSION_INFO', sportsbookGetTemptoken);
            window.Sportsbook.setListener('ANALYTICS_EVENT', (event) => publicEventsAPIService.trigger('sportsbook-analytics', event));
        } catch (error) {
            log('log', '[SPORTSBOOK | Portal] Set listeners error', error);
        }
    }, [
        dispatch,
        isRenderingStarted,
        isAppInitialized,
        showLoginPopup,
        sportsbookRootURL,
        sportsbookGetTemptoken,
        isLoggedIn,
        navigate,
        loginPage
    ]);

    useEffect(() => {
        if (!isAppInitialized) {
            return;
        }

        try {
            window.Sportsbook.setListener('SHOW_DEPOSIT', () => {
                if (htcmdService.isEnabled) {
                    htcmdService.openDeposit();
                } else {
                    navigate(depositPage);
                }
            });
        } catch (error) {
            log('log', '[SPORTSBOOK | Portal] Set listeners error', error);
        }
    }, [depositPage, isAppInitialized, navigate]);

    useEffect(() => {
        if (!isAppInitialized) {
            return;
        }

        try {
            window.Sportsbook.setListener('NAVIGATE', (url, options) => {
                if (url.startsWith('native://')) {
                    htcmdService.sendMessage(url.substr(9), options);

                    return;
                }

                if (isExternalUrl(url) && htcmdService.isEnabled) {
                    htcmdService.openExternalPage(url);

                    return;
                }

                switch (options?.target) {
                    case '_blank':
                        openWindow(url);
                        break;

                    default:
                        navigate(url);
                }
            });
        } catch (error) {
            log('log', '[SPORTSBOOK | Portal] Set listeners error', error);
        }
    }, [isAppInitialized, navigate]);

    useEffect(() => {
        if (!isSessionStarted || !isAppInitialized || !isRenderingStarted) {
            return;
        }

        window.Sportsbook.setListener('IMS_GET_TAGS', async (tagNames) => {
            await addSubscription({
                windowSessionId: windowSessionId ?? -1,
                subscriptionTags: tagNames
            });

            const { tags } = await getPlayerTags({ tagNames });

            window.Sportsbook.sendMessage('IMS_SET_TAGS', tags);

            sportsTagsRef.current = tagNames;
        });
    }, [
        isRenderingStarted,
        sportsTagsRef,
        isAppInitialized,
        isSessionStarted,
        windowSessionId,
        getPlayerTags,
        addSubscription
    ]);

    /**
     * This listener is used to handle player tag changes, but it triggers message to Sports
     * only in case if the tag name is in the list of tag names that were requested before.
     */
    usePushSubscription(31133, () => (pushMessage) => {
        if (!isAppInitialized || !isRenderingStarted) {
            return;
        }

        const { notificationPlayerTagChangeInfo } = pushMessage?.data?.notification || {};

        if (!notificationPlayerTagChangeInfo) {
            return;
        }

        const {
            tag,
            value,
            removed
        } = notificationPlayerTagChangeInfo;

        if (!sportsTagsRef.current.includes(tag)) {
            return;
        }

        window.Sportsbook.sendMessage('IMS_UPDATE_TAGS', [
            {
                tag,
                value,
                removed
            }
        ]);
    }, [
        isRenderingStarted,
        isAppInitialized,
        sportsTagsRef
    ]);
};
